import type { Pinia } from "pinia"
import type { App } from "vue"
import type { Router } from "vue-router"

import * as SentryVue from "@sentry/vue"

import { envConfig, ERROR_REPORT, getGlobalData, isMicroApp, isProd, SENTRY_DSN, VITE_APP_VERSION } from "./config"

/**
 * Sentry实例（兼容独立打开和微前端模式）
 */
const Sentry = getGlobalData()?.getSentry() || SentryVue

/**
 * 获取Sentry
 * @deprecated 请使用 getGlobalData 获取Sentry实例
 * @returns
 */
export const getSentry = () => {
  if (!ERROR_REPORT) return null
  return Sentry
}

// 定义需要忽略的请求路径RegExp
const IgnoredPaths = [
  /https:\/\/arms-retcode\.aliyuncs\.com\/r\.png/,
  /aliyuncs\.com\/logstores/,
  /locales\//,
  /api\/main\/common\/getI18nDict/,
  /schema\.json/, // 忽略 schema.json 文件
  /pluginWebUpdateNotice\/web_version_by_plugin.json/, // 忽略 pluginWebUpdateNotice/web_version_by_plugin.json 文件
]

// 定义需要忽略的 span 请求路径（用于性能追踪过滤）
const IgnoredSpanUrls: RegExp[] = [
  // 忽略静态资源
  /\.(js|css|png|jpg|jpeg|gif|svg|ico|woff|woff2|ttf|eot)(\?.*)?$/i,
  // 忽略第三方服务
  /aliyuncs\.com/,
  /locales\//,
  /arms-retcode/,
  /cdn\./,
  /static\./,
  // 忽略频繁的接口
  /api\/main\/common\/getI18nDict/,
  /schema\.json/,
  /web_version_by_plugin\.json/,
]
// 定义需要忽略的资源路径RegExp
const IgnoredResourcePaths: RegExp[] = [
  /\.json[^/]?$/,
  /.svg$/,
]

// 定义需要忽略的控制台输出内容
const IgnoredConsoleMessages = [
  // /Vue DevTools/i,
  /injection "Symbol\(\)" not found\./,
  /^Translation key/,
  /^\[PageSpy\]/,
]

// 定义需要忽略的错误
const IgnoredErrors = [
  "Network Error",
  "AuthError",
  "等待子页面就绪超时",
  "环境配置获取失败",
  "Importing a module script failed.",
  "Java bridge method invocation error",
  "TypeError: Load failed",
  /`Script error\.`/,
  /Load failed \([^.]+\.[^.]+\.[^.]+\)/,
  /Failed to fetch/,
  /Object captured as promise rejection with keys: \w+,/,
  /Object captured as exception with keys: \w+,/,
  /Request failed with status code [45]\d{2}/,
  /Failed to fetch dynamically imported module/,
  /Unable to preload CSS for/,
  /ResizeObserver loop completed with undelivered notifications/,
  /ResizeObserver loop limit exceeded/,
  /Failed to fetch dynamically imported module:/,
  /timeout of \d+ms exceeded/,
  "Failed to execute 'unobserve' on 'ResizeObserver': parameter 1 is not of type 'Element'.",
  /Non-Error promise rejection captured with value: (undefined|null)/,
  /NetworkError when attempting to fetch resource./,
  "undefined",
  "All promises were rejected",
  /evaluating 'iframeElement\.contentDocument\.addEventListener'/,
]
/**
 * Sentry实例
 */
let sentry: ReturnType<typeof SentryVue.init> | null = null
if (ERROR_REPORT) {
  sentry = Sentry.init({
    dsn: SENTRY_DSN,
    environment: envConfig.VITE_APP_ENV,
    release: VITE_APP_VERSION,
    // 性能追踪采样率 - 生产环境降低到，减少 spans 数量
    tracesSampleRate: isProd ? 0.1 : 0.2,
    // 限制面包屑数量，减少内存占用
    maxBreadcrumbs: isProd ? 50 : 30,
    // 性能监控传输配置 - 使用 fetch 且设置超时
    transportOptions: {
      // 发送事件前的缓冲时间（ms），批量发送减少请求次数
      bufferSize: 80,
      // 请求超时时间
      fetchOptions: {
        // 使用 keepalive 避免阻塞页面卸载
        keepalive: true,
      },
    },
    ignoreErrors: IgnoredErrors,
    denyUrls: ["pagespy.zdsoft.cloud/"],
    // Filter out default `Vue` integration
    integrations: integrations =>
      integrations.filter(integration => integration.name !== "Vue"),
    // beforeBreadcrumb(breadcrumb, hint) {
    //   return breadcrumb.category === "ui.click" ? null : breadcrumb;
    // },
    beforeBreadcrumb(breadcrumb: SentryVue.Breadcrumb, _hint?: SentryVue.BreadcrumbHint) {
      // 过滤网络请求面包屑
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        const url = breadcrumb.data?.url || ""

        // 检查是否匹配忽略的请求路径
        if (IgnoredPaths.some(path => path.test(url))) {
          return null
        }

        // 检查是否匹配忽略的资源路径
        if (IgnoredResourcePaths.some(path => path.test(url))) {
          return null
        }
      }

      // 过滤控制台输出面包屑
      if (breadcrumb.category === "console") {
        // 过滤所有 console.log 类型的面包屑
        if (["log", "debug"].includes(breadcrumb.level as string)) {
          return null
        }

        const message = breadcrumb.message || ""

        // 检查是否匹配忽略的控制台消息
        if (IgnoredConsoleMessages.some(pattern => pattern.test(message))) {
          return null
        }
      }

      // 过滤导航面包屑（可选：屏蔽特定路由）
      if (breadcrumb.category === "navigation") {
        const to = breadcrumb.data?.to || ""

        // 可以添加需要忽略的路由路径
        const ignoredRoutes = [
          /\/debug/,
          /\/test/,
          // 添加其他需要忽略的路由
        ]

        if (ignoredRoutes.some(route => route.test(to))) {
          return null
        }
      }

      return breadcrumb
    },
    // 添加请求过滤逻辑
    beforeSend(event, hint) {
      // 过滤掉代码分割加载错误（通常是网络问题导致的）
      if (event.exception?.values?.[0]?.type === "ChunkLoadError") {
        return null
      }

      // 过滤掉特定的请求错误
      const error = hint.originalException
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase()

        // 过滤掉取消的请求
        if (errorMessage.includes("canceled") || errorMessage.includes("aborted")) {
          return null
        }
      }

      // // 过滤掉特定的网络错误
      // if (event.contexts?.response?.status_code === 0) {
      //   // 网络中断或CORS错误
      //   return null
      // }

      return event
    },
    // 添加事务过滤逻辑，大幅减少 spans 数量
    beforeSendTransaction(event) {
      // 过滤掉不重要的事务
      const transactionName = event.transaction || ""

      // 1. 过滤掉静态资源加载的事务
      if (IgnoredSpanUrls.some(pattern => pattern.test(transactionName))) {
        return null
      }

      // 2. 过滤事务中的 spans
      if (event.spans) {
        event.spans = event.spans.filter(span => {
          const spanDescription = span.description || ""

          // 过滤静态资源和第三方请求的 spans
          if (IgnoredSpanUrls.some(pattern => pattern.test(spanDescription))) {
            return false
          }

          // 过滤状态码为 2xx 或 3xx 且响应时间小于 100ms 的快速请求
          // 这些通常不需要追踪
          if (span.op === "http.client" || span.op === "fetch") {
            const data = span.data || {}
            const statusCode = data["http.response.status_code"] || data.status_code
            const statusCodeNum = typeof statusCode === "number" ? statusCode : Number.parseInt(String(statusCode), 10)
            const duration = span.timestamp && span.start_timestamp
              ? (span.timestamp - span.start_timestamp) * 1000
              : 0

            // 过滤快速且成功的请求
            if (!Number.isNaN(statusCodeNum) && statusCodeNum >= 200 && statusCodeNum < 400 && duration < 100) {
              return false
            }
          }

          return true
        })

        // 3. 限制 spans 数量 - 只保留最重要的 spans
        if (event.spans.length > 30) {
          // 按持续时间排序，保留耗时最长的 30 个 spans
          event.spans = event.spans
            .sort((a, b) => {
              const durationA = a.timestamp && a.start_timestamp ? a.timestamp - a.start_timestamp : 0
              const durationB = b.timestamp && b.start_timestamp ? b.timestamp - b.start_timestamp : 0
              return durationB - durationA
            })
            .slice(0, 30)
        }
      }

      // 4. 过滤掉超快的页面导航事务（可能是预加载或缓存命中）
      if (event.type === "transaction" && event.contexts?.trace?.op === "pageload") {
        const duration = event.timestamp && event.start_timestamp
          ? (event.timestamp - event.start_timestamp) * 1000
          : 0

        // 过滤小于 50ms 的页面加载（通常是缓存或不重要的页面）
        if (duration < 50) {
          return null
        }
      }

      return event
    },
  })
}
/**
 * 配置Sentry上下文(只在微前端模式下设置)
 */
const setupSentryContext = (app: App) => {
  if (!ERROR_REPORT || !isMicroApp) return
  Sentry.withScope(scope => {
    scope.setTag("appInstanceId", app._uid)
    scope.setTag("app", "accounts")
    scope.setTag("url", window.location.href)
  })
}
/**
 * 配置Vue sentry
 */
export const setupVueSentry = (app: App) => {
  if (!ERROR_REPORT) return
  Sentry.addIntegration(Sentry.vueIntegration({
    app,
    tracingOptions: {
      /**
       * 是否跟踪组件 - 生产环境关闭以提升性能
       * 如需追踪特定组件，可以在组件内手动添加追踪
       */
      trackComponents: !isProd,
      // 只跟踪 mount 和 unmount，移除高频的 update 钩子
      hooks: ["mount", "unmount"],
      // 设置超时时间，避免长时间追踪
      timeout: 2000,
    },
    // 设置附加数据大小限制
    attachProps: !isProd, // 生产环境不附加 props
  }))
  setupSentryContext(app)
}

/**
 * 配置Vue Router sentry
 */
export const setupVueRouterSentry = (router: Router) => {
  if (!ERROR_REPORT) return
  Sentry.addIntegration(
    Sentry.browserTracingIntegration({
      router,
      // 关闭长任务追踪，减少 spans 数量
      enableLongTask: false,
      // 关闭 INP 追踪，减少交互 spans
      enableInp: false,
    }),
  )
}

/**
 * 配置Pinia sentry
 */
export const setupPiniaSentry = (pinia: Pinia) => {
  if (!ERROR_REPORT) return
  pinia.use(Sentry.createSentryPiniaPlugin())
}

export const setSentryUser = (user: SentryVue.User) => {
  if (!ERROR_REPORT) return
  Sentry.setUser(user)
}

/**
 * 获取Sentry实例，方便在其他地方使用
 */
export const getSentryInstance = () => {
  if (!ERROR_REPORT) return null
  return sentry || null
}

/**
 * 清除Sentry上下文
 */
export const cleanSentryContext = () => {
  if (!ERROR_REPORT) return
  if (Sentry) {
    Sentry.withScope(scope => {
      scope.setTags({
        appInstanceId: undefined,
        app: undefined,
        url: undefined,
      })
    })
  }
}
